import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useHistory, useLocation } from "react-router";
import { AxiosRequestConfig } from "axios";
import { FormikHelpers } from "formik";
import { intersection, orderBy, uniqBy } from "lodash";

import { CUSTOM_PANELS, DESIGN_DETAILS } from "helpers/apis";
import useAxios from "helpers/axios";
import CustomizeConfirmDialog from "pages/evaluate-panel-options/CustomizeConfirmDialog";
import { CnvStatus, DesignType, IDesign } from "types/design";
import { IDesignDetail } from "types/designDetail";
import { IDiseaseType } from "types/diseaseType";
import { ICustomPanelForm } from "types/form";
import { ICustomPanel } from "types/panel";
import { IWorkflow } from "types/workflow";

import useCatalog from "./useCatalog";

type DraftAlertStatus = "success" | "error" | null;

interface ICustomPanelActionProps {
  values: ICustomPanelForm;
  actions: Partial<FormikHelpers<ICustomPanelForm>>;
  confirmed?: Boolean;
  callback?: Function;
  errorCallback?: Function;
  config?: AxiosRequestConfig;
}

interface ICatalogDetail {
  catalog?: IDesign | null;
  customize: boolean;
  diseaseType?: IDiseaseType;
  draftAlertStatus: DraftAlertStatus;
  draftSaved?: ICustomPanel;
  foundGenes: string;
  originFoundGenes: string;
  isCustomPanel: boolean;
  isDraftCustomPanel: boolean;
  isSuggestedPanel: boolean;
  newDetails: Map<number, INewCustomPanel>;
  tmbToggle: boolean;
  workflow?: IWorkflow;
  customPanelAction: (params: ICustomPanelActionProps) => void;
  fetchDesignDetails: Function;
  setCatalog: Function;
  setCustomize: Function;
  setDraftAlertStatus: React.Dispatch<React.SetStateAction<DraftAlertStatus>>;
  setIsCustomPanel: Function;
  setDiseaseType: Function;
  setDraftSaved: React.Dispatch<React.SetStateAction<ICustomPanel | undefined>>;
  setFoundGenes: Function;
  setOriginFoundGenes: Function;
  setNewDetails: Function;
  setWorkflow: Function;
}

export interface INewCustomPanel {
  cnvToggle: boolean; // It's a flag for toggle enable/disable
  cnv: boolean; // It's a toggle value
  designDetails: IDesignDetail[];
}

export const CatalogDetailContext = createContext<ICatalogDetail>(
  {} as ICatalogDetail
);

export function CatalogDetailProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [customize, setCustomize] = useState<boolean>(false);
  const [isCustomPanel, setIsCustomPanel] = useState<boolean>(false); // A flat to show whether a current one is a catalog panel or custom panel
  const [catalog, setCatalog] = useState<IDesign | null>();
  const [newDetails, setNewDetails] = useState<Map<number, INewCustomPanel>>(
    new Map()
  ); // New design details to be added from different designs
  // Panel Detail Confirm
  const [resolve, setResolve] = useState<{
    fn: (result: boolean) => void;
  } | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  //
  const [workflow, setWorkflow] = useState<IWorkflow>();
  const [isEvaluation, setIsEvaluation] = useState<boolean>(false);
  const [diseaseType, setDiseaseType] = useState<IDiseaseType>();
  const [foundGenes, setFoundGenes] = useState<string>("");
  const [originFoundGenes, setOriginFoundGenes] = useState<string>(""); // It does not include any panel genes during customization
  const [isDraftCustomPanel, setIsDraftCustomPanel] = useState(false); // True if it's on draft custom panel view or edit page
  const [draftAlertStatus, setDraftAlertStatus] =
    useState<DraftAlertStatus>(null); // Flag to be used to display an alert after a custom panel is drafted or failed
  const [draftSaved, setDraftSaved] = useState<ICustomPanel>(); // Saved draft custom panel
  const isSuggestedPanel = useMemo(
    () => catalog?.designType === DesignType.SUGGESTED_PANEL,
    [catalog?.designType]
  );
  const tmbToggle = useMemo(() => {
    return (
      intersection(
        [...newDetails.keys()],
        catalog?.panelspresent
          ?.filter((panel) => panel.tmb)
          .map((panel) => panel.design)
      ).length > 0
    );
  }, [newDetails, catalog?.panelspresent]);
  const { pathname } = useLocation();
  const history = useHistory();
  const { setCustomPanels } = useCatalog();
  // Fetch design details by panels
  const { fetchData } = useAxios({
    config: {
      url: DESIGN_DETAILS,
    },
    callOnInit: false,
  });
  const { current: fetchDesignDetails } = useRef(
    (panels: { panelId: number; cnv?: boolean }[]) => {
      fetchData(
        { params: { designId: panels.map((panel) => panel.panelId).join() } },
        ({ response }: any) => {
          if (response !== null && Array.isArray(response.data)) {
            const newDetails = new Map();
            orderBy(response.data, ["gene"], ["asc"]).forEach(
              (designDetail) => {
                const detail = newDetails.get(designDetail.design);
                if (detail) {
                  newDetails.set(designDetail.design, {
                    ...detail,
                    designDetails: [...detail.designDetails, designDetail],
                  });
                } else {
                  const panel = panels.find(
                    (item) => item.panelId === designDetail.design
                  )!;
                  newDetails.set(designDetail.design, {
                    cnvToggle: designDetail.cnv === CnvStatus.YES_NO,
                    cnv:
                      typeof panel.cnv === "boolean"
                        ? panel.cnv
                        : designDetail.cnv === CnvStatus.YES,
                    designDetails: [designDetail],
                  });
                }
              }
            );
            setNewDetails(
              (details: Map<number, INewCustomPanel>) =>
                new Map([...newDetails, ...details])
            );
          }
        }
      );
    }
  );

  // Reset all the changes out of the custom panel page
  useEffect(() => {
    if (pathname === "/evaluate-panel-options") {
      // Clear catalog on evaluate panel options page to avoid caching issue
      // https://invitae.jira.com/browse/MKPL-915
      setCatalog(null);
      setDraftSaved(undefined);
    }
    if (
      pathname.indexOf("/evaluate-panel-options/catalog-panel-customize/") !== 0
    ) {
      setNewDetails(new Map());
    }

    if (pathname.indexOf("/evaluate-panel-options") === 0) {
      setIsEvaluation(true);
    } else {
      setIsEvaluation(false);
    }
    if (pathname.indexOf("/dashboard/draft-custom-panels/") === 0) {
      setIsDraftCustomPanel(true);
    } else {
      setIsDraftCustomPanel(false);
    }
  }, [pathname]);

  useEffect(() => {
    setNewDetails(new Map());
  }, [catalog?.id]);

  // Remove draft alert message if new details are changed
  useEffect(() => {
    setDraftAlertStatus(null);
  }, [newDetails]);

  // Panel Detail Confirm
  const confirm = useCallback((errorMessage) => {
    return new Promise((fn) => {
      setConfirmMessage(errorMessage);
      setResolve({ fn });
    }).then((val) => {
      setResolve(null);
      return val;
    });
  }, []);

  const customPanelAction = useCallback(
    ({
      values,
      actions,
      callback,
      errorCallback,
      confirmed,
      config = {},
    }: ICustomPanelActionProps) => {
      fetchData(
        {
          url: CUSTOM_PANELS,
          method: "POST",
          data: {
            panelspresent: [
              ...(isSuggestedPanel || isCustomPanel
                ? []
                : [{ design: catalog?.id, cnv: catalog?.cnvToggle }]),
              ...Array.from(newDetails, (data: any) => ({
                design: data[0],
                cnv: data[1]?.cnv,
              })),
            ],
            name: values.panelName,
            workflow: workflow?.id,
            diseaseType: diseaseType?.id,
            ...("msi" in values && { hasMsi: values.msi }),
            ...("snpId" in values && { hasSnpId: values.snpId }),
            ...("status" in values && { status: values.status }),
            ctfPassed: confirmed || undefined,
            targets: foundGenes,
          },
          ...config,
        },
        ({ response }: any) => {
          if (callback) {
            callback(response);
            return;
          }
          fetchData(
            {
              method: "GET",
              url: `${CUSTOM_PANELS}${response.data.id}`,
              params: { targets: originFoundGenes },
            },
            ({ response }: any) => {
              if (response.data) {
                setCustomPanels((customPanels: Partial<IDesign>[]) =>
                  uniqBy(
                    [{ ...response.data, draftSaved }, ...customPanels],
                    "id"
                  )
                );
                history.push("/evaluate-panel-options");
              }
            }
          );
        },
        async ({ error, status, dataError }: any) => {
          if (errorCallback) {
            errorCallback({ error, status, dataError });
            return;
          }
          if (status === 409 && !confirmed) {
            const result = await confirm(dataError);
            if (result) {
              customPanelAction({
                values,
                actions,
                callback,
                config,
                confirmed: true,
              });
            }
            return;
          }
          if (actions.setFieldError) {
            actions.setFieldError(
              "panels",
              error ??
                "Panel must consist of a minimum of 20 primers and 3 genes."
            );
          }
        },
        () => {
          if (actions.setSubmitting) {
            actions.setSubmitting(false);
          }
        }
      );
    },
    [
      isSuggestedPanel,
      isCustomPanel,
      catalog,
      draftSaved,
      foundGenes,
      originFoundGenes,
      history,
      newDetails,
      workflow,
      diseaseType,
      confirm,
      fetchData,
      setCustomPanels,
    ]
  );

  const memoedValue = useMemo(
    () => ({
      catalog,
      customize,
      diseaseType,
      draftAlertStatus,
      draftSaved,
      foundGenes,
      tmbToggle,
      originFoundGenes,
      isCustomPanel,
      isDraftCustomPanel,
      isSuggestedPanel,
      newDetails,
      workflow,
      customPanelAction,
      fetchDesignDetails,
      setCatalog,
      setCustomize,
      setDiseaseType,
      setDraftAlertStatus,
      setDraftSaved,
      setFoundGenes,
      setOriginFoundGenes,
      setIsCustomPanel,
      setNewDetails,
      setWorkflow,
    }),
    [
      catalog,
      customize,
      diseaseType,
      draftAlertStatus,
      draftSaved,
      foundGenes,
      tmbToggle,
      originFoundGenes,
      isCustomPanel,
      isDraftCustomPanel,
      isSuggestedPanel,
      newDetails,
      workflow,
      customPanelAction,
      fetchDesignDetails,
    ]
  );

  return (
    <CatalogDetailContext.Provider value={memoedValue}>
      {children}
      {!!resolve && (
        <CustomizeConfirmDialog
          resolve={resolve}
          message={confirmMessage}
          confirmText={isEvaluation ? "Compare" : "Confirm request"}
        />
      )}
    </CatalogDetailContext.Provider>
  );
}

export default function useCatalogDetail() {
  return useContext(CatalogDetailContext);
}
